/**
 * Card "Postazioni" nel tab Dettagli della pagina negozio.
 * OPS (settings.edit_brands) aggiunge/elimina postazioni con progressivo
 * numerico automatico; gli altri ruoli vedono la lista in sola lettura.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Armchair } from 'lucide-react';
import { stationsApi } from '../lib/api';
import { useHasPermission } from '../lib/usePermissions';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const StoreStationsEditor = ({ storeId }) => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const canManage = useHasPermission('inventory.stations.manage');

  const load = useCallback(() => {
    stationsApi.list(storeId)
      .then(r => setStations(r.data?.stations || []))
      .catch(() => setStations([]))
      .finally(() => setLoading(false));
  }, [storeId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  async function addStation() {
    setBusy(true);
    try {
      await stationsApi.create(storeId, {});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function removeStation(st) {
    const nItems = st.items?.length || 0;
    const msg = nItems > 0
      ? `Eliminare "${st.name}"? I ${nItems} oggetti assegnati torneranno liberi.`
      : `Eliminare "${st.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await stationsApi.remove(st.station_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  }

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="store-stations-editor">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading text-base font-semibold inline-flex items-center gap-2">
          <Armchair className="w-4 h-4" /> Postazioni ({stations.length})
        </h3>
        {canManage && (
          <Button size="sm" variant="outline" onClick={addStation} disabled={busy}
                  className="rounded-none h-7 text-xs" data-testid="station-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">Numerazione progressiva automatica. Gli oggetti si assegnano dalla tab "Postazioni".</p>
      {loading ? (
        <p className="text-xs text-slate-400">Caricamento…</p>
      ) : stations.length === 0 ? (
        <p className="text-xs text-slate-500">Nessuna postazione configurata.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {stations.map(st => (
            <span key={st.station_id}
                  className={`inline-flex items-center gap-1.5 text-xs bg-white border border-slate-300 py-1 ${canManage ? 'pl-2.5 pr-1' : 'px-2.5'}`}
                  data-testid={`station-chip-${st.station_id}`}>
              <span className="font-semibold">{st.name}</span>
              {(st.items?.length || 0) > 0 && (
                <span className="text-slate-400">· {st.items.length} ogg.</span>
              )}
              {canManage && (
                <button type="button" onClick={() => removeStation(st)}
                        className="p-1 text-slate-400 hover:text-red-600"
                        data-testid={`station-delete-${st.station_id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
